import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { formatUsDate } from "../utils/completeByDate";
import "../styles/date-picker-field.css";

type Props = {
    /** Controlled value as a US date string (MM/DD/YYYY). Empty string when unset. */
    value: string;
    onChange: (usText: string) => void;
    disabled?: boolean;
    inputId?: string;
    inputTestId?: string;
    placeholder?: string;
    ariaLabel?: string;
};

type Coords = {
    left: number;
    width: number;
    top: number | null;
    bottom: number | null;
};

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

// Approximate rendered height of the popover, used to decide flip direction.
const POPOVER_EST_HEIGHT = 372;
const POPOVER_WIDTH = 320;
const VIEWPORT_MARGIN = 8;

function pad(n: number): string {
    return String(n).padStart(2, "0");
}

function dateToIso(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function daysInMonth(year: number, monthIndex: number): number {
    return new Date(year, monthIndex + 1, 0).getDate();
}

/** Parses a full, valid MM/DD/YYYY string into a local Date, else null. */
function parseUsToDate(value: string): Date | null {
    const match = US_DATE.exec(value.trim());
    if (!match) {
        return null;
    }
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month - 1)) {
        return null;
    }
    return new Date(year, month - 1, day);
}

function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

export default function DatePickerField({
    value,
    onChange,
    disabled = false,
    inputId,
    inputTestId,
    placeholder = "MM/DD/YYYY",
    ariaLabel = "Date",
}: Props) {
    const [open, setOpen] = useState(false);
    const [viewDate, setViewDate] = useState<Date>(() => parseUsToDate(value) ?? new Date());
    const [coords, setCoords] = useState<Coords>({ left: 0, width: POPOVER_WIDTH, top: 0, bottom: null });

    const anchorRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const selectedDate = useMemo(() => parseUsToDate(value), [value]);
    const today = useMemo(() => new Date(), []);

    // When opening, jump the visible month to the selected date (or today).
    useEffect(() => {
        if (open) {
            setViewDate(parseUsToDate(value) ?? new Date());
        }
    }, [open, value]);

    // Position the popover relative to the input, flipping above when there is
    // not enough room below. Clamped to the viewport so it never overflows.
    const updatePosition = useCallback(() => {
        const anchor = anchorRef.current;
        if (!anchor) {
            return;
        }
        const rect = anchor.getBoundingClientRect();
        const width = Math.min(POPOVER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
        let left = rect.left;
        if (left + width > window.innerWidth - VIEWPORT_MARGIN) {
            left = window.innerWidth - VIEWPORT_MARGIN - width;
        }
        if (left < VIEWPORT_MARGIN) {
            left = VIEWPORT_MARGIN;
        }
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < POPOVER_EST_HEIGHT + VIEWPORT_MARGIN && rect.top > spaceBelow;
        if (openUp) {
            setCoords({ left, width, top: null, bottom: window.innerHeight - rect.top + VIEWPORT_MARGIN });
        } else {
            setCoords({ left, width, top: rect.bottom + VIEWPORT_MARGIN, bottom: null });
        }
    }, []);

    useLayoutEffect(() => {
        if (!open) {
            return;
        }
        updatePosition();
        const onReflow = () => updatePosition();
        window.addEventListener("scroll", onReflow, true);
        window.addEventListener("resize", onReflow);
        return () => {
            window.removeEventListener("scroll", onReflow, true);
            window.removeEventListener("resize", onReflow);
        };
    }, [open, updatePosition]);

    // Close on outside click / Escape.
    useEffect(() => {
        if (!open) {
            return;
        }
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                anchorRef.current?.contains(target) ||
                popoverRef.current?.contains(target)
            ) {
                return;
            }
            setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.stopPropagation();
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKey, true);
        };
    }, [open]);

    const weeks = useMemo(() => {
        const year = viewDate.getFullYear();
        const month = viewDate.getMonth();
        const firstOfMonth = new Date(year, month, 1);
        const gridStart = new Date(year, month, 1 - firstOfMonth.getDay());
        const cells: Date[] = [];
        for (let i = 0; i < 42; i++) {
            cells.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
        }
        const rows: Date[][] = [];
        for (let i = 0; i < 6; i++) {
            rows.push(cells.slice(i * 7, i * 7 + 7));
        }
        return rows;
    }, [viewDate]);

    const goToMonth = (delta: number) => {
        setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

    const selectDay = (day: Date) => {
        onChange(formatUsDate(dateToIso(day)));
        setOpen(false);
    };

    const popover = open && !disabled ? (
        <div
            ref={popoverRef}
            className="dpf-popover"
            role="dialog"
            aria-label="Choose date"
            style={{
                left: coords.left,
                width: coords.width,
                top: coords.top ?? undefined,
                bottom: coords.bottom ?? undefined,
            }}
        >
            <div className="dpf-header">
                <button
                    type="button"
                    className="dpf-nav-btn"
                    onClick={() => goToMonth(-1)}
                    aria-label="Previous month"
                >
                    ‹
                </button>
                <div className="dpf-title" aria-live="polite">
                    {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
                </div>
                <button
                    type="button"
                    className="dpf-nav-btn"
                    onClick={() => goToMonth(1)}
                    aria-label="Next month"
                >
                    ›
                </button>
            </div>

            <div className="dpf-weekdays">
                {WEEKDAYS.map((wd) => (
                    <div key={wd} className="dpf-weekday">
                        {wd}
                    </div>
                ))}
            </div>

            <div className="dpf-grid">
                {weeks.map((week, wi) => (
                    <div key={wi} className="dpf-week">
                        {week.map((day) => {
                            const inMonth = day.getMonth() === viewDate.getMonth();
                            const isSelected = selectedDate != null && isSameDay(day, selectedDate);
                            const isToday = isSameDay(day, today);
                            const classes = [
                                "dpf-day",
                                inMonth ? "" : "dpf-day--muted",
                                isSelected ? "dpf-day--selected" : "",
                                isToday && !isSelected ? "dpf-day--today" : "",
                            ]
                                .filter(Boolean)
                                .join(" ");
                            return (
                                <button
                                    key={dateToIso(day)}
                                    type="button"
                                    className={classes}
                                    onClick={() => selectDay(day)}
                                    aria-label={formatUsDate(dateToIso(day))}
                                    aria-pressed={isSelected}
                                >
                                    {day.getDate()}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </div>

            <div className="dpf-footer">
                <button
                    type="button"
                    className="dpf-footer-btn"
                    onClick={() => {
                        onChange("");
                        setOpen(false);
                    }}
                >
                    Clear
                </button>
                <button
                    type="button"
                    className="dpf-footer-btn dpf-footer-btn--accent"
                    onClick={() => selectDay(new Date())}
                >
                    Today
                </button>
            </div>
        </div>
    ) : null;

    return (
        <div className="dpf-root">
            <div className="dpf-input-row" ref={anchorRef}>
                <input
                    id={inputId}
                    type="text"
                    inputMode="numeric"
                    className="dpf-input"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    disabled={disabled}
                    autoComplete="off"
                    aria-label={ariaLabel}
                    data-testid={inputTestId}
                />
                {!disabled && (
                    <button
                        type="button"
                        className="dpf-calendar-btn"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Open calendar"
                        aria-haspopup="dialog"
                        aria-expanded={open}
                        data-testid={inputTestId ? `${inputTestId}-calendar-btn` : undefined}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                            <line x1="16" y1="2" x2="16" y2="6" />
                            <line x1="8" y1="2" x2="8" y2="6" />
                            <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                    </button>
                )}
            </div>

            {popover != null && createPortal(popover, document.body)}
        </div>
    );
}
